import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Btn, Input, Textarea } from "../components/ui";
import { authFetch, apiUrl } from "../utils/api";
import { showGlobalToast } from "../utils/globalNotify";

type FileRow = { id: number; filename: string; byte_size: number };
type EmailTemplate = { id: number; name: string; subject: string; body: string; updated_at?: string; files?: FileRow[] };
type Placeholder = { key: string; token: string; label: string; group: string };

function humanSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function FileUploadButton({ onFile, children }: { onFile: (f: File) => void; children: React.ReactNode }) {
  const ref = useRef<HTMLInputElement>(null);
  return (
    <>
      <Btn variant="secondary" className="py-[7px] text-xs" onClick={() => ref.current?.click()}>{children}</Btn>
      <input ref={ref} type="file" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f); e.target.value = ""; }} />
    </>
  );
}

function Modal({ title, onClose, children, wide }: { title: string; onClose: () => void; children: React.ReactNode; wide?: boolean }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [onClose]);
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-auto bg-black/50 p-4" onClick={onClose}>
      <div className={`mt-[6vh] w-full ${wide ? "max-w-3xl" : "max-w-xl"} rounded-[16px] border border-[var(--line)] bg-white shadow-[0_24px_60px_rgba(16,32,38,.28)]`} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between gap-3 border-b border-[var(--line)] px-5 py-3">
          <div className="text-base font-extrabold tracking-[-.02em] text-[var(--ink)]">{title}</div>
          <Btn variant="secondary" className="py-1 text-xs" onClick={onClose}>Schließen</Btn>
        </div>
        <div className="max-h-[74vh] overflow-auto p-5">{children}</div>
      </div>
    </div>,
    document.body,
  );
}

function PlaceholderChips({ placeholders }: { placeholders: Placeholder[] }) {
  const groups = Array.from(new Set(placeholders.map((p) => p.group)));
  return (
    <div className="grid gap-2">
      <div className="text-xs font-bold uppercase tracking-wide text-[var(--ink-muted)]">Platzhalter (klicken zum Kopieren)</div>
      {groups.map((g) => (
        <div key={g}>
          <div className="mb-1 text-[.72rem] font-bold text-[var(--teal-dark)]">{g}</div>
          <div className="flex flex-wrap gap-1.5">
            {placeholders.filter((p) => p.group === g).map((p) => (
              <button key={p.key} type="button" title={p.label}
                onClick={() => { navigator.clipboard?.writeText(p.token); showGlobalToast(`${p.token} kopiert`, "success"); }}
                className="rounded-[6px] border border-[var(--line)] bg-[var(--surface)] px-2 py-1 font-mono text-[.72rem] text-[var(--ink-mid)] hover:border-[var(--teal-mid)] hover:text-[var(--teal-dark)]">
                {p.token}
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ---- E-Mail-Vorlagen verwalten --------------------------------------------
type Editing = { id?: number; name: string; subject: string; body: string; files?: FileRow[] };

export function EmailTemplatesModal({ onClose }: { onClose: () => void }) {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [placeholders, setPlaceholders] = useState<Placeholder[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Editing | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await authFetch(apiUrl("/api/v1/email_templates"));
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      setTemplates(data.templates || []);
      setPlaceholders(data.placeholders || []);
    } catch {
      showGlobalToast("Vorlagen konnten nicht geladen werden.", "error");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const save = async () => {
    if (!editing) return;
    if (!editing.name.trim() || !editing.subject.trim()) { showGlobalToast("Name und Betreff angeben.", "warning"); return; }
    setSaving(true);
    try {
      const method = editing.id ? "PATCH" : "POST";
      const url = editing.id ? `/api/v1/email_templates/${editing.id}` : "/api/v1/email_templates";
      const resp = await authFetch(apiUrl(url), {
        method, headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email_template: { name: editing.name, subject: editing.subject, body: editing.body } }),
      });
      if (!resp.ok) { const m = await resp.json().catch(() => ({})); throw new Error((m.errors || []).join(" ") || `HTTP ${resp.status}`); }
      const saved = await resp.json();
      showGlobalToast("Vorlage gespeichert.", "success");
      // Editor offen lassen (mit id), damit Dateien angehängt werden können.
      setEditing({ id: saved.id, name: saved.name, subject: saved.subject, body: saved.body, files: saved.files || [] });
      await load();
    } catch (e) {
      showGlobalToast("Speichern fehlgeschlagen. " + (e instanceof Error ? e.message : ""), "error");
    } finally {
      setSaving(false);
    }
  };

  const addFile = async (file: File) => {
    if (!editing?.id) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      const resp = await authFetch(apiUrl(`/api/v1/email_templates/${editing.id}/files`), { method: "POST", body: fd });
      if (!resp.ok) { const m = await resp.json().catch(() => ({})); throw new Error((m.errors || []).join(" ") || `HTTP ${resp.status}`); }
      const data = await resp.json();
      setEditing((e) => (e ? { ...e, files: data.files } : e));
      await load();
    } catch (e) {
      showGlobalToast("Anhang fehlgeschlagen. " + (e instanceof Error ? e.message : ""), "error");
    }
  };

  const removeFile = async (fileId: number) => {
    if (!editing?.id) return;
    try {
      const resp = await authFetch(apiUrl(`/api/v1/email_templates/${editing.id}/files/${fileId}`), { method: "DELETE" });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      setEditing((e) => (e ? { ...e, files: data.files } : e));
      await load();
    } catch {
      showGlobalToast("Entfernen fehlgeschlagen.", "error");
    }
  };

  const remove = async (t: EmailTemplate) => {
    if (!window.confirm(`Vorlage „${t.name}" löschen?`)) return;
    try {
      const resp = await authFetch(apiUrl(`/api/v1/email_templates/${t.id}`), { method: "DELETE" });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      await load();
    } catch {
      showGlobalToast("Löschen fehlgeschlagen.", "error");
    }
  };

  return (
    <Modal title="E-Mail-Vorlagen" onClose={onClose} wide>
      {editing ? (
        <div className="grid gap-3">
          <div className="grid gap-1">
            <label className="text-xs text-[var(--ink-muted)]">Name (intern)</label>
            <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
          </div>
          <div className="grid gap-1">
            <label className="text-xs text-[var(--ink-muted)]">Betreff</label>
            <Input value={editing.subject} onChange={(e) => setEditing({ ...editing, subject: e.target.value })} />
          </div>
          <div className="grid gap-1">
            <label className="text-xs text-[var(--ink-muted)]">Text</label>
            <Textarea rows={9} value={editing.body} onChange={(e) => setEditing({ ...editing, body: e.target.value })} />
          </div>
          <PlaceholderChips placeholders={placeholders} />
          <div className="rounded-[10px] border border-[var(--line)] p-3">
            <div className="mb-2 text-xs font-bold uppercase tracking-wide text-[var(--ink-muted)]">Anhänge</div>
            {editing.id ? (
              <div className="grid gap-2">
                {(editing.files || []).length === 0 ? (
                  <div className="text-xs text-[var(--ink-muted)]">Noch keine Anhänge.</div>
                ) : (
                  <div className="grid gap-1.5">
                    {(editing.files || []).map((f) => (
                      <div key={f.id} className="flex items-center gap-2 rounded-[8px] bg-[var(--surface)] px-2.5 py-1.5">
                        <span className="min-w-0 flex-1 truncate text-xs text-[var(--ink-mid)]">{f.filename} · {humanSize(f.byte_size)}</span>
                        <button type="button" className="text-xs font-semibold text-[#B33636] hover:underline" onClick={() => removeFile(f.id)}>Entfernen</button>
                      </div>
                    ))}
                  </div>
                )}
                <div><FileUploadButton onFile={addFile}>Datei anhängen</FileUploadButton></div>
              </div>
            ) : (
              <div className="text-xs text-[var(--ink-muted)]">Vorlage zuerst speichern, dann können Dateien angehängt werden.</div>
            )}
          </div>
          <div className="flex gap-2">
            <Btn onClick={save} disabled={saving}>{saving ? "Speichern…" : "Speichern"}</Btn>
            <Btn variant="secondary" onClick={() => setEditing(null)}>{editing.id ? "Zurück zur Liste" : "Abbrechen"}</Btn>
          </div>
        </div>
      ) : (
        <div className="grid gap-3">
          <div>
            <Btn onClick={() => setEditing({ name: "", subject: "", body: "" })}>Neue Vorlage</Btn>
          </div>
          {loading ? (
            <div className="text-sm text-[var(--ink-muted)]">Lädt…</div>
          ) : templates.length === 0 ? (
            <div className="text-sm text-[var(--ink-muted)]">Noch keine Vorlagen.</div>
          ) : (
            <div className="grid gap-2">
              {templates.map((t) => (
                <div key={t.id} className="flex flex-wrap items-center gap-3 rounded-[10px] border border-[var(--line)] p-3">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-bold text-[var(--ink)]">{t.name}</div>
                    <div className="truncate text-xs text-[var(--ink-muted)]">{t.subject}{(t.files?.length ?? 0) > 0 ? ` · ${t.files!.length} Anhang/Anhänge` : ""}</div>
                  </div>
                  <Btn variant="secondary" className="py-[7px] text-xs" onClick={() => setEditing({ id: t.id, name: t.name, subject: t.subject, body: t.body, files: t.files || [] })}>Bearbeiten</Btn>
                  <button type="button" className="text-xs font-semibold text-[#B33636] hover:underline" onClick={() => remove(t)}>Löschen</button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

// ---- E-Mail an mehrere Unternehmen (Auswahl) -------------------------------
export function BulkSendEmailModal({ businessIds, onClose, onSent }: { businessIds: number[]; onClose: () => void; onSent?: () => void }) {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [placeholders, setPlaceholders] = useState<Placeholder[]>([]);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [templateId, setTemplateId] = useState<number | null>(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const resp = await authFetch(apiUrl("/api/v1/email_templates"));
        if (!resp.ok) return;
        const data = await resp.json();
        setTemplates(data.templates || []);
        setPlaceholders(data.placeholders || []);
      } catch { /* ignore */ }
    })();
  }, []);

  const applyTemplate = (id: string) => {
    const t = templates.find((x) => String(x.id) === id);
    setTemplateId(t ? t.id : null);
    if (t) { setSubject(t.subject); setBody(t.body); }
  };
  const selectedTemplate = templates.find((x) => x.id === templateId);

  const send = async () => {
    if (!subject.trim()) { showGlobalToast("Betreff angeben.", "warning"); return; }
    if (!window.confirm(`E-Mail wirklich an ${businessIds.length} Unternehmen senden?`)) return;
    setSending(true);
    try {
      const resp = await authFetch(apiUrl("/api/v1/businesses/send_bulk_email"), {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ business_ids: businessIds, subject, body, email_template_id: templateId }),
      });
      if (!resp.ok) { const m = await resp.json().catch(() => ({})); throw new Error((m.errors || []).join(" ") || `HTTP ${resp.status}`); }
      const data = await resp.json();
      const sent = (data.sent || []).length;
      const skipped = (data.skipped || []).length;
      showGlobalToast(`${sent} E-Mail(s) gesendet${skipped ? `, ${skipped} übersprungen (keine Kontakt-E-Mail)` : ""}.`, skipped ? "warning" : "success");
      onSent?.();
      onClose();
    } catch (e) {
      showGlobalToast("Versand fehlgeschlagen. " + (e instanceof Error ? e.message : ""), "error");
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal title={`E-Mail an ${businessIds.length} Unternehmen`} onClose={onClose} wide>
      <div className="grid gap-3">
        <div className="rounded-[10px] border border-[var(--line)] bg-[var(--surface)] px-3 py-2 text-xs text-[var(--ink-mid)]">
          Dieselbe Nachricht geht an alle ausgewählten Unternehmen. Platzhalter wie {"{{firma_name}}"} werden je Unternehmen individuell ersetzt. Empfänger ist die jeweilige Kontakt-E-Mail; Unternehmen ohne Kontakt-E-Mail werden übersprungen.
        </div>
        {templates.length > 0 && (
          <div className="grid gap-1">
            <label className="text-xs text-[var(--ink-muted)]">Vorlage übernehmen</label>
            <select className="h-11 rounded-xl border border-[var(--line)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[rgba(0,159,181,.25)]" defaultValue="" onChange={(e) => applyTemplate(e.target.value)}>
              <option value="">— Vorlage wählen —</option>
              {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
        )}
        {selectedTemplate && (selectedTemplate.files?.length ?? 0) > 0 && (
          <div className="rounded-[8px] bg-[var(--surface)] px-3 py-2 text-xs text-[var(--ink-mid)]">
            Anhänge der Vorlage werden mitgesendet: {selectedTemplate.files!.map((f) => f.filename).join(", ")}
          </div>
        )}
        <div className="grid gap-1">
          <label className="text-xs text-[var(--ink-muted)]">Betreff</label>
          <Input value={subject} onChange={(e) => setSubject(e.target.value)} />
        </div>
        <div className="grid gap-1">
          <label className="text-xs text-[var(--ink-muted)]">Text</label>
          <Textarea rows={8} value={body} onChange={(e) => setBody(e.target.value)} />
        </div>
        <PlaceholderChips placeholders={placeholders} />
        <div className="flex flex-wrap gap-2">
          <Btn onClick={send} disabled={sending}>{sending ? "Sende…" : `An ${businessIds.length} senden`}</Btn>
          <Btn variant="secondary" onClick={onClose}>Abbrechen</Btn>
        </div>
      </div>
    </Modal>
  );
}

// ---- E-Mail an ein Unternehmen senden --------------------------------------
export function SendEmailModal({ businessId, businessName, defaultTo, onClose }: { businessId: number; businessName: string; defaultTo?: string; onClose: () => void }) {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [placeholders, setPlaceholders] = useState<Placeholder[]>([]);
  const [mode, setMode] = useState<"new" | "template">("new");
  const [to, setTo] = useState(defaultTo || "");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [templateId, setTemplateId] = useState<number | null>(null);
  const [preview, setPreview] = useState<{ to: string; subject: string; body: string } | null>(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const resp = await authFetch(apiUrl("/api/v1/email_templates"));
        if (!resp.ok) return;
        const data = await resp.json();
        setTemplates(data.templates || []);
        setPlaceholders(data.placeholders || []);
      } catch { /* ignore */ }
    })();
  }, []);

  const applyTemplate = (id: string) => {
    const t = templates.find((x) => String(x.id) === id);
    setTemplateId(t ? t.id : null);
    if (t) { setSubject(t.subject); setBody(t.body); setPreview(null); }
  };
  const selectedTemplate = templates.find((x) => x.id === templateId);

  const doPreview = async () => {
    try {
      const resp = await authFetch(apiUrl(`/api/v1/businesses/${businessId}/email_preview`), {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ subject, body }),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      setPreview({ to: to || data.to, subject: data.subject, body: data.body });
    } catch {
      showGlobalToast("Vorschau fehlgeschlagen.", "error");
    }
  };

  const send = async () => {
    if (!subject.trim()) { showGlobalToast("Betreff angeben.", "warning"); return; }
    const recipient = to.trim();
    if (!recipient) { showGlobalToast("Empfänger-Adresse angeben.", "warning"); return; }
    if (!window.confirm(`E-Mail wirklich an ${recipient} senden?`)) return;
    setSending(true);
    try {
      const resp = await authFetch(apiUrl(`/api/v1/businesses/${businessId}/send_email`), {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ to: recipient, subject, body, email_template_id: templateId }),
      });
      if (!resp.ok) { const m = await resp.json().catch(() => ({})); throw new Error((m.errors || []).join(" ") || `HTTP ${resp.status}`); }
      const data = await resp.json();
      showGlobalToast(`E-Mail an ${data.to} gesendet.`, "success");
      onClose();
    } catch (e) {
      showGlobalToast("Versand fehlgeschlagen. " + (e instanceof Error ? e.message : ""), "error");
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal title={`E-Mail an ${businessName}`} onClose={onClose} wide>
      <div className="grid gap-3">
        <div className="grid gap-1">
          <label className="text-xs text-[var(--ink-muted)]">Empfänger</label>
          <Input value={to} onChange={(e) => setTo(e.target.value)} placeholder="kontakt@…" />
        </div>

        {/* Auswahl: neue E-Mail schreiben oder Vorlage verwenden */}
        <div className="inline-flex w-fit rounded-[10px] border border-[var(--line)] bg-[var(--surface)] p-1">
          <button
            type="button"
            onClick={() => { setMode("new"); setTemplateId(null); setPreview(null); }}
            className={mode === "new"
              ? "rounded-[7px] bg-white px-3.5 py-1.5 text-[13px] font-semibold text-[var(--teal-dark)] shadow-[0_1px_2px_rgba(16,32,38,.08)]"
              : "rounded-[7px] px-3.5 py-1.5 text-[13px] font-semibold text-[var(--ink-muted)] transition-colors hover:text-[var(--ink-mid)]"}
          >
            Neue E-Mail schreiben
          </button>
          <button
            type="button"
            onClick={() => setMode("template")}
            className={mode === "template"
              ? "rounded-[7px] bg-white px-3.5 py-1.5 text-[13px] font-semibold text-[var(--teal-dark)] shadow-[0_1px_2px_rgba(16,32,38,.08)]"
              : "rounded-[7px] px-3.5 py-1.5 text-[13px] font-semibold text-[var(--ink-muted)] transition-colors hover:text-[var(--ink-mid)]"}
          >
            Vorlage verwenden
          </button>
        </div>

        {mode === "template" && (
          templates.length > 0 ? (
            <div className="grid gap-1">
              <label className="text-xs text-[var(--ink-muted)]">Vorlage wählen</label>
              <select
                className="h-11 rounded-xl border border-[var(--line)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[rgba(0,159,181,.25)]"
                value={templateId ?? ""}
                onChange={(e) => applyTemplate(e.target.value)}
              >
                <option value="">— Vorlage wählen —</option>
                {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
          ) : (
            <div className="rounded-[8px] bg-[var(--surface)] px-3 py-2 text-xs text-[var(--ink-mid)]">
              Noch keine E-Mail-Vorlagen vorhanden. Lege welche über „E-Mail-Vorlagen" an.
            </div>
          )
        )}
        {selectedTemplate && (selectedTemplate.files?.length ?? 0) > 0 && (
          <div className="rounded-[8px] bg-[var(--surface)] px-3 py-2 text-xs text-[var(--ink-mid)]">
            Anhänge der Vorlage werden mitgesendet: {selectedTemplate.files!.map((f) => f.filename).join(", ")}
          </div>
        )}
        <div className="grid gap-1">
          <label className="text-xs text-[var(--ink-muted)]">Betreff</label>
          <Input value={subject} onChange={(e) => { setSubject(e.target.value); setPreview(null); }} />
        </div>
        <div className="grid gap-1">
          <label className="text-xs text-[var(--ink-muted)]">Text</label>
          <Textarea rows={8} value={body} onChange={(e) => { setBody(e.target.value); setPreview(null); }} />
        </div>
        <PlaceholderChips placeholders={placeholders} />

        {preview && (
          <div className="rounded-[10px] border border-[var(--line)] bg-[var(--surface)] p-3">
            <div className="mb-1 text-xs font-bold uppercase tracking-wide text-[var(--ink-muted)]">Vorschau (Platzhalter ersetzt)</div>
            <div className="text-sm font-semibold text-[var(--ink)]">{preview.subject}</div>
            <div className="mt-1 whitespace-pre-wrap text-sm text-[var(--ink-mid)]">{preview.body}</div>
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          <Btn variant="secondary" onClick={doPreview}>Vorschau</Btn>
          <Btn onClick={send} disabled={sending}>{sending ? "Sende…" : "E-Mail senden"}</Btn>
        </div>
      </div>
    </Modal>
  );
}
